import { useCurrentUser } from '@cma/app'
import {
  AccountOption,
  Button,
  Flash,
  FormField,
  Input,
  Link
} from '@cma/common'
import {
  AccountBanner,
  PlatformLoginButton,
  SelectAccountCard,
  UseDifferentEmailButton,
  platformLauncherUrl,
  requiresPlatformLogin,
  useAccountOptions,
  useLogin
} from '@cma/features/auth'
import { CloudIcon } from '@cma/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

type Step = 'email' | 'select' | 'password'

const schema = yup.object({
  email: yup.string().email().required('Your email is required'),
  password: yup.string().when('$step', {
    is: 'password',
    then: (field) => field.required('Your password is required')
  })
})

interface LocationState {
  from?: {
    pathname?: string
  }
  passwordResetRequested?: boolean
}

export default function Login() {
  const { data: { currentUser } = {} } = useCurrentUser()
  const [step, setStep] = useState<Step>('email')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string>()
  const [lookupError, setLookupError] = useState<string>()
  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    clearErrors,
    formState: { errors }
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    context: { step },
    defaultValues: { email: '', password: '' }
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [showResetSuccess, setShowResetSuccess] = useState(
    !!(location.state as LocationState | null)?.passwordResetRequested
  )
  const {
    mutate: lookupAccounts,
    isLoading: isLookingUp,
    reset: resetLookup
  } = useAccountOptions()
  const {
    mutate: login,
    isLoading: isLoggingIn,
    error: loginError,
    reset: resetLogin
  } = useLogin()
  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname || '/'
  const selectedAccount = accounts.find(
    ({ accountId }) => accountId === selectedAccountId
  )
  const didPickAccount = accounts.length > 1 && !!selectedAccount
  const shouldFocusEmail = useRef(false)

  useEffect(() => {
    if (step === 'password') {
      setFocus('password')
    } else if (step === 'email' && shouldFocusEmail.current) {
      shouldFocusEmail.current = false
      setFocus('email')
    }
  }, [step, setFocus])

  function goToPasswordStep(options: AccountOption[], account: AccountOption) {
    if (requiresPlatformLogin(account)) {
      window.location.href = platformLauncherUrl
      return
    }

    setAccounts(options)
    setSelectedAccountId(account.accountId)
    setStep('password')
  }

  function findAccounts(email: string) {
    setSubmittedEmail(email)
    setLookupError(undefined)
    setAccounts([])
    setSelectedAccountId(undefined)
    resetLogin()

    lookupAccounts(email, {
      onSuccess(options) {
        if (!options.length) {
          setAccounts([])
          setSelectedAccountId(undefined)
          setStep('password')
          return
        }

        if (options.length === 1) {
          goToPasswordStep(options, options[0])
          return
        }

        setAccounts(options)
        setStep('select')
      },
      onError(error) {
        setLookupError(error.message)
        setStep('email')
      }
    })
  }

  function startOver() {
    shouldFocusEmail.current = true
    setStep('email')
    setAccounts([])
    setSelectedAccountId(undefined)
    setLookupError(undefined)
    resetLookup()
    resetLogin()
    clearErrors()
    setValue('email', submittedEmail)
    setValue('password', '')
  }

  if (currentUser) {
    return <Navigate to={redirectTo} />
  }

  if (step === 'select') {
    return (
      <SelectAccountCard
        accounts={accounts}
        email={submittedEmail}
        onContinue={(account) => goToPasswordStep(accounts, account)}
        onUseDifferentEmail={startOver}
      />
    )
  }

  return (
    <>
      <div className="space-y-4 rounded-lg bg-white px-8 py-6 shadow">
        <h1 className="mb-6 text-center text-2xl font-medium">Sign in</h1>
        <PlatformLoginButton />
        <a
          href={`${process.env.VITE_APP_CAS_URL}/app/external_login/cloud_cma?continue_to=${process.env.VITE_APP_CMA_APP_URL}${redirectTo}`}
          className="flex items-center hover:no-underline">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-bl rounded-tl bg-suite-600 text-white">
            <CloudIcon className="h-5 w-5" />
          </span>
          <span className="flex h-9 w-full items-center justify-center rounded-br rounded-tr !border border-l-0 !border-solid border-suite-600 px-2 text-center text-sm font-medium text-suite-600">
            Sign in with Cloud Agent Suite
          </span>
        </a>
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true">
            <div className="h-0.5 w-full bg-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-xs font-medium uppercase text-gray-500">
              Or
            </span>
          </div>
        </div>
        <form
          className="space-y-4"
          onSubmit={handleSubmit((data) => {
            if (step === 'password') {
              login(
                {
                  email: data.email,
                  password: data.password ?? '',
                  accountId: selectedAccountId
                },
                {
                  onSuccess() {
                    navigate(
                      (location?.state as { from?: string })?.from ?? '/'
                    )
                  }
                }
              )
              return
            }

            findAccounts(data.email)
          })}>
          <FormField label="Email" error={errors.email?.message}>
            <Input
              {...register('email', {
                onChange() {
                  setShowResetSuccess(false)
                }
              })}
              type="email"
              readOnly={step === 'password'}
            />
          </FormField>
          {didPickAccount && (
            <AccountBanner>
              Signing in with {selectedAccount?.name}
            </AccountBanner>
          )}
          {step === 'password' && (
            <FormField label="Password" error={errors.password?.message}>
              <Input
                {...register('password', {
                  onChange() {
                    if (loginError) resetLogin()
                  }
                })}
                type="password"
              />
            </FormField>
          )}
          <Button
            fullWidth
            loading={step === 'password' ? isLoggingIn : isLookingUp}>
            Continue
          </Button>
          {showResetSuccess && step === 'email' && (
            <Flash variant="success">
              You will receive an email with instructions about how to reset
              your password in a few minutes.
            </Flash>
          )}
          {!!lookupError && step === 'email' && (
            <div
              role="alert"
              aria-live="polite"
              className="text-center text-sm text-red-500">
              {lookupError}
            </div>
          )}
          {!!loginError && step === 'password' && (
            <div
              role="alert"
              aria-live="polite"
              className="text-center text-sm text-red-500">
              {loginError.message}
            </div>
          )}
          {step === 'password' && (
            <div className="flex">
              <Link
                to={`/password/new?${new URLSearchParams({
                  email: submittedEmail,
                  ...(selectedAccountId
                    ? { accountId: selectedAccountId }
                    : {}),
                  ...(selectedAccount
                    ? { accountName: selectedAccount.name }
                    : {})
                }).toString()}`}
                className="helix-text-brand mx-auto text-xs font-medium hover:no-underline">
                Forgot Password?
              </Link>
            </div>
          )}
        </form>
      </div>
      {step === 'password' && <UseDifferentEmailButton onClick={startOver} />}
    </>
  )
}
